/**
 * The payee page's Transactions tab (TMMAPP-0123 §D; TMMAPP-0124 items 15 and 20). The lines in the
 * range, with paging (Load more is disabled while a page loads). The compact AI mark stays on a
 * line AI named (`payeeSource: AI`, TMM-B9); `MANUAL` and `ALIAS` carry none. A line that serves
 * how its name was made (`namingSource`, TMMAPP-0164 item 10) says "Named by: {label}" instead, with
 * the drawer's labels, tooltips and "confirmed by you" rule (`NamedBy`); absent, nothing is said.
 *
 * For the owner, a line with its served `accountId` and `transactionId` (TMMAPI-0160 U4) can move:
 * each row's menu has **Move to another payee…**, row checkboxes feed **Move selected…**, and the
 * row's date opens the transaction drawer (`AllocateDialog`) in place. A line without them has no
 * menu, no checkbox and a plain date.
 */
import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import type { Payee, PayeeLine } from '@/api/payees';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { AiPill } from '@/components/AiPill';
import { MoneyText } from '@/components/MoneyText';
import { AllocateDialog } from '@/features/accounts/AllocateDialog';
import { canEditAllocations, isOwnerAccount } from '@/features/accounts/accountAccess';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { OverflowMenu } from '@/shell/OverflowMenu';

import { MovePayeeDialog } from './MovePayeeDialog';
import { NamedBy } from './NamingSourcePill';

interface PayeeTransactionsProps {
  payee: Payee;
  lines: PayeeLine[];
  currency: string | null;
  readOnly: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Re-reads the payee (quietly) after a move or a change in the drawer. */
  onChanged: () => Promise<void>;
  onNotify: (message: string) => void;
}

export function PayeeTransactions({
  payee,
  lines,
  currency,
  readOnly,
  hasMore,
  loadingMore,
  onLoadMore,
  onChanged,
  onNotify,
}: PayeeTransactionsProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());
  const [moving, setMoving] = useState<PayeeLine[] | null>(null);
  const [drawer, setDrawer] = useState<{ account: Account; line: OwnerRegisterLine } | null>(null);
  const [opening, setOpening] = useState(false);
  const movable = readOnly ? [] : lines;
  // Only lines still listed count as selected (a move or a refetch can take one away).
  const chosen = movable.filter((line) => selected.has(line.transactionId));

  function toggle(line: PayeeLine, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(line.transactionId);
      else next.delete(line.transactionId);
      return next;
    });
  }

  async function openDrawer(line: PayeeLine) {
    if (opening) return;
    setOpening(true);
    try {
      const [account, register] = await Promise.all([
        accountsApi.get(line.accountId),
        transactionsApi.get(line.accountId, line.transactionId),
      ]);
      setDrawer({ account, line: register });
    } catch (err) {
      onNotify(errorCopy(t, err, 'accounts.errorGeneric'));
    } finally {
      setOpening(false);
    }
  }

  const dateOf = (line: PayeeLine) => {
    const date = formatCivilDate(line.postedOn, locale);
    if (readOnly) return date;
    return (
      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={() => void openDrawer(line)}
        aria-label={t('payee.openLine', { date, bank: line.payee })}
        sx={{ verticalAlign: 'baseline' }}
      >
        {date}
      </Link>
    );
  };

  const menuFor = (line: PayeeLine) =>
    !readOnly ? (
      <OverflowMenu
        label={t('payee.lineActions', { date: formatCivilDate(line.postedOn, locale) })}
        title={line.payee}
      >
        {(close) => (
          <MenuItem
            onClick={() => {
              close();
              setMoving([line]);
            }}
          >
            {t('payee.move.menu')}
          </MenuItem>
        )}
      </OverflowMenu>
    ) : null;

  const checkFor = (line: PayeeLine) =>
    !readOnly ? (
      <Checkbox
        size="small"
        checked={selected.has(line.transactionId)}
        onChange={(_event, on) => toggle(line, on)}
        slotProps={{
          input: {
            'aria-label': t('payee.selectLine', {
              date: formatCivilDate(line.postedOn, locale),
              bank: line.payee,
            }),
          },
        }}
      />
    ) : null;

  const aiMark = (line: PayeeLine) =>
    // How the name was made, when served (item 10): its own AI pill replaces the compact mark.
    line.namingSource !== undefined ? (
      <Typography
        variant="caption"
        component="span"
        sx={{ color: 'text.secondary' }}
        data-payee-line-named-by=""
      >
        <NamedBy source={line.namingSource} confirmedByPerson={line.confirmedByPerson === true} />
      </Typography>
    ) : // TMM-B9: the line was named by AI (its served payeeSource).
    line.payeeSource === 'AI' ? (
      <AiPill compact describes="payeeName" />
    ) : null;

  return (
    <Stack spacing={1}>
      {movable.length > 0 && (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={chosen.length === 0}
            onClick={() => setMoving(chosen)}
          >
            {t('payee.move.selected', { count: chosen.length })}
          </Button>
        </Stack>
      )}
      {md ? (
        <Table size="small" aria-label={t('payee.tabTransactions')}>
          <TableHead>
            <TableRow>
              {movable.length > 0 && (
                <TableCell padding="checkbox">
                  <Box component="span" sx={visuallyHidden}>
                    {t('payee.selectColumn')}
                  </Box>
                </TableCell>
              )}
              <TableCell>{t('aiHistory.colWhen')}</TableCell>
              <TableCell>{t('nav.accounts')}</TableCell>
              <TableCell align="right">{t('payee.colTotal')}</TableCell>
              <TableCell>{t('payee.byEnvelope')}</TableCell>
              {movable.length > 0 && <ActionsHeaderCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.transactionId} data-payee-line={line.transactionId}>
                {movable.length > 0 && <TableCell padding="checkbox">{checkFor(line)}</TableCell>}
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flexWrap: 'wrap',
                    }}
                  >
                    {dateOf(line)}
                    {aiMark(line)}
                  </Box>
                </TableCell>
                <TableCell>{line.accountName}</TableCell>
                <TableCell align="right">
                  <MoneyText minor={line.amountMinor} currency={currency} />
                </TableCell>
                <TableCell>{line.envelopes.map((env) => env.name).join(', ')}</TableCell>
                {movable.length > 0 && <TableCell>{menuFor(line)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Stack spacing={1}>
          {lines.map((line) => (
            <Box
              key={line.transactionId}
              data-payee-line={line.transactionId}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                py: 1,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              {checkFor(line)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  component="div"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
                >
                  <span>{dateOf(line)}</span>
                  <span>{t('payee.lineAccount', { account: line.accountName })}</span>
                  {aiMark(line)}
                </Typography>
                <MoneyText minor={line.amountMinor} currency={currency} />
              </Box>
              {menuFor(line)}
            </Box>
          ))}
        </Stack>
      )}
      {lines.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('payee.noLines')}
        </Typography>
      )}
      {hasMore && (
        <Button onClick={onLoadMore} disabled={loadingMore} sx={{ alignSelf: 'flex-start' }}>
          {loadingMore ? t('common.loading') : t('payee.loadMore')}
        </Button>
      )}
      {moving !== null && (
        <MovePayeeDialog
          payee={payee}
          lines={moving}
          onClose={() => setMoving(null)}
          onMoved={async () => {
            setSelected(new Set());
            await onChanged();
          }}
          onNotify={onNotify}
        />
      )}
      {drawer !== null && (
        <OwnedEnvelopesProvider>
          <AllocateDialog
            account={drawer.account}
            line={drawer.line}
            editor={me.tier === 'SUBSCRIBER' && canEditAllocations(drawer.account)}
            owner={isOwnerAccount(drawer.account)}
            onClose={() => setDrawer(null)}
            onChanged={async (line) => {
              setDrawer((prev) => (prev !== null ? { ...prev, line } : prev));
              await onChanged();
            }}
            onNotify={onNotify}
          />
        </OwnedEnvelopesProvider>
      )}
    </Stack>
  );
}
